import React, { useMemo } from 'react';
import { useElementSize } from '@mantine/hooks';
import _ from 'lodash';

import { FunctionUtilization } from '@hud/portal-dtos/webapp-function.dto';
import { Container } from '@hud/web-components/Container/Container';
import {
  Treemap as BaseTreemap,
  TreeNode,
} from '@hud/web-components/Charts/Treemap/Treemap';
import { TreemapLegend } from '@hud/web-components/Charts/Treemap/TreemapLegend';
import Skeleton from '@hud/web-components/Skeleton/Skeleton';
import { TreemapTooltip } from './TreemapTooltip';

export interface TreemapProps {
  loading: boolean;
  functions: FunctionUtilization[];

  onFunctionClick: (functionId: number) => void;
}

const FACTOR = 0.2;
const TREEMAP_HEIGHT = 500;

export interface FileNodeData {
  filePath: string;
  linesOfCode: number;
  lastChangeAt: Date | null;
  mappedFunctions: number;
}

export type NodeData = FunctionUtilization | FileNodeData;

export function isFileNodeData(data: NodeData): data is FileNodeData {
  // if data is a file node, it will have a mappedFunctions property
  return (data as FileNodeData).mappedFunctions !== undefined;
}

export function isFunctionNodeData(
  data: NodeData,
): data is FunctionUtilization {
  // if data is a function node, it will have a totalEndpoints property
  return (data as FunctionUtilization).totalEndpoints !== undefined;
}

export default function Treemap({
  loading,
  functions,
  onFunctionClick,
}: TreemapProps) {
  const { ref, width } = useElementSize();

  const tree = useMemo<TreeNode<NodeData> | undefined>(() => {
    if (!functions.length) return;
    const minNormalizedRuns = _.minBy(functions, 'normalizedRuns')
      ?.normalizedRuns as number;
    const maxNormalizedRuns = _.maxBy(functions, 'normalizedRuns')
      ?.normalizedRuns as number;
    const weightRuns = (normalizedRuns: number) => {
      if (normalizedRuns === 0) return 0;
      const normalizedWeight =
        (normalizedRuns - minNormalizedRuns) /
        (maxNormalizedRuns - minNormalizedRuns);
      return FACTOR + normalizedWeight * (1 - FACTOR);
    };

    let groupedFiles: TreeNode<NodeData>[] = _(functions)
      .groupBy('filePath')
      .toPairs()
      .map(([filePath, fileFunctions]) => {
        const averageNormalizedRuns = _.meanBy(fileFunctions, 'normalizedRuns');
        const weight = weightRuns(averageNormalizedRuns);
        const totalLines = _.sumBy(fileFunctions, 'linesOfCode');

        const children: TreeNode<NodeData>[] = fileFunctions.map((f) => {
          const name =
            f.totalEndpoints > 0
              ? f.totalEndpoints > 1
                ? `API (${f.totalEndpoints})`
                : `${f.endpoints[0].method.toLocaleUpperCase()} ${
                    f.endpoints[0].path
                  }`
              : f.signature;
          return {
            id: `${f.id}`,
            name,
            value: f.linesOfCode,
            weight: weightRuns(f.normalizedRuns),
            data: f,
            options: {},
          };
        });

        // normalizedPath (just file name without path)
        const normalizedPath = filePath.split('/').pop() as string;

        return {
          id: filePath,
          name: normalizedPath,
          value: totalLines,
          weight,
          children,
          options: {
            reverseEllipsis: true,
          },
          data: {
            filePath,
            linesOfCode: totalLines,
            lastChangeAt:
              _.maxBy(fileFunctions, 'lastChangeAt')?.lastChangeAt || null,
            mappedFunctions: fileFunctions.length,
          } satisfies FileNodeData,
        } satisfies TreeNode<NodeData>;
      })
      .sortBy('value')
      .reverse()
      .value();

    const totalLinesOfCode = _.sumBy(groupedFiles, 'value');

    groupedFiles = groupedFiles.filter(
      (node) => node.value / totalLinesOfCode > 0.005,
    );

    // for files that are less then 1% of the total lines of code, don't display inner functions
    groupedFiles = groupedFiles.map((node) => {
      if (node.value / totalLinesOfCode < 0.009) {
        return {
          ...node,
          children: [
            {
              id: node.id + '-small-file-filler',
              name: '',
              value: node.value,
              weight: 0,
              data: undefined,
              options: {
                filler: true,
              },
            },
          ],
        };
      }
      return node;
    });

    return {
      id: 'root',
      value: 0,
      name: '',
      weight: 0,
      children: groupedFiles.map((node) => ({ ...node, value: 0 })),
    } satisfies TreeNode<NodeData>;
  }, [functions]);

  function handleClick(node: TreeNode<NodeData>) {
    if (!node.id) return;

    // Unsupported
    if (node.id.startsWith('more')) return;

    // Filepath: Unsupported
    if (node.data === undefined) {
      // const searchParams = createSearchParams({ files: node.id });
      // navigate(`/explore/functions?${globalFilters}&${searchParams}`);
    }

    if (node.data && isFunctionNodeData(node.data)) {
      // navigate(`/explore/functions/${node.id}?${globalFilters}`);
      onFunctionClick?.(node.data.id);
    }
  }

  return (
    <Container
      title={
        <>
          <div>Code</div>
          <TreemapLegend />
        </>
      }
      ref={ref}
    >
      {loading ? (
        <Skeleton width={width} height={TREEMAP_HEIGHT} />
      ) : (
        tree && (
          <BaseTreemap<NodeData>
            width={width}
            height={TREEMAP_HEIGHT}
            data={tree}
            onClick={handleClick}
            LeafTooltipComponent={TreemapTooltip}
          />
        )
      )}
    </Container>
  );
}
